import React, { useState } from 'react';
import { AdminAccount, orgManagementService } from '../services/orgManagementService';

interface Props {
  admin: AdminAccount;
  onClose: () => void;
  onSaved: (username: string) => void;
}

const ResetAdminPasswordModal: React.FC<Props> = ({ admin, onClose, onSaved }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (newPassword.length < 8 || newPassword.length > 72) {
      setError('新密码长度须为8-72位');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await orgManagementService.resetAdminPassword(admin.id, newPassword);
      onSaved(admin.username);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || '重置密码失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-[1px]"
      onClick={saving ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-admin-password-title"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-slate-100 bg-slate-50/80 px-6 py-5">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined rounded-xl bg-blue-100 p-2 text-blue-700">key</span>
            <div>
              <h2 id="reset-admin-password-title" className="text-lg font-semibold text-slate-900">重置管理员密码</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                目标账号：<span className="font-medium text-slate-700">{admin.username}</span>
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
          <div>
            <label htmlFor="reset-admin-new-password" className="mb-1.5 block text-sm font-medium text-slate-700">新密码</label>
            <input
              id="reset-admin-new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              disabled={saving}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
              placeholder="请输入8-72位新密码"
            />
          </div>

          <div>
            <label htmlFor="reset-admin-confirm-password" className="mb-1.5 block text-sm font-medium text-slate-700">确认新密码</label>
            <input
              id="reset-admin-confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              disabled={saving}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
              placeholder="请再次输入新密码"
            />
          </div>

          <p className="text-xs leading-5 text-slate-500">重置后，原密码将无法用于后续登录。</p>
          {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? '重置中...' : '确认重置'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetAdminPasswordModal;
